import React from 'react';
import { NavLink, NavLinkProps } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// MUI
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Icon from '@mui/material/Icon';
import Tooltip from '@mui/material/Tooltip';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { Typography, useTheme } from '@mui/material';

// CUSTOM
import { MenuType } from '@/@types/common';

// Types
type NavDrawerLinkProps = React.PropsWithChildren<{
  open: boolean;
  to: string;
  // eslint-disable-next-line react/no-unused-prop-types
  text: string;
  icon: string;
  ttoken: string;
  // eslint-disable-next-line react/no-unused-prop-types
  nested?: MenuType[];
  collapse?: () => void;
  collapsed?: boolean;
  level: number;
  name: string;
  bold?: boolean;
}>;

const NavDrawerLink = (props: NavDrawerLinkProps) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const { open, to, icon, ttoken, collapse, collapsed, level, name, bold } = props;

  type MyNavLinkProps = Omit<NavLinkProps, 'to'>;

  const MyNavLink = React.useMemo(
    () =>
      // eslint-disable-next-line react/no-unstable-nested-components
      React.forwardRef<HTMLAnchorElement, MyNavLinkProps>((navLinkProps, ref) => {
        const { className: previousClasses, ...rest } = navLinkProps;
        const elementClasses = previousClasses?.toString() ?? '';
        return (
          <NavLink
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...rest}
            key={`nav-link-${name}`}
            ref={ref}
            to={collapse ? '' : to}
            end
            className={({ isActive }) => (isActive && collapse === undefined ? `${elementClasses} Mui-selected` : elementClasses)}
            style={({ isActive }) => ({
              transition: 'box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
              boxShadow: isActive && collapse === undefined ? 'inset 3px 0 0 0 ' + theme.palette.primary.light : undefined,
            })}
          />
        );
      }),
    [to]
  );

  return (
    <ListItemButton
      sx={{ paddingY: 1 }}
      key={`nav-list-item-${name}`}
      aria-label={`nav-list-item-${name}`}
      component={MyNavLink}
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      onClick={() => {
        collapse !== undefined ? collapse() : null;
      }}
    >
      <ListItemIcon
        sx={{
          marginLeft: open ? 8 * level + 'px' : '0px',
          transition: 'margin-left 0.3s',
          minWidth: '42px',
        }}
      >
        {open ? (
          <Icon fontSize="small" color="primary">
            {icon}
          </Icon>
        ) : (
          <Tooltip title={t(ttoken) as string}>
            <Icon fontSize="small" color="primary">
              {icon}
            </Icon>
          </Tooltip>
        )}
      </ListItemIcon>
      <ListItemText slotProps={{ primary: { style: { fontWeight: bold ? 600 : 400 } } }} primary={t(ttoken)} sx={{ m: 0 }} />
      {collapse !== undefined ? collapsed !== undefined && collapsed === true ? <ExpandLess /> : <ExpandMore /> : <></>}
    </ListItemButton>
  );
};

export default NavDrawerLink;
